<template>
    <div class="company-information">
        <div class="company-information__columns">
            <Card>
                <Heading
                    :labelTitle="$t.__('CompanyInformation.legal_information.title')"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'building-skyscraper', color: 'brand' }"
                />
                <CompanyInformationLoader v-if="isLoading" />
                <template v-else>
                    <CompanyInformationFormLocked
                        v-if="companyInformationLocked"
                        :data="companyInformation"
                        :countries="countries"
                    />
                    <CompanyInformationForm
                        v-else
                        :data="companyInformation"
                        :countries="countries"
                        @update="handleCompanyInformationUpdate"
                    />
                </template>
            </Card>
            <Card>
                <Heading
                    :labelTitle="$t.__('CompanyInformation.contact_information.title')"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'user-hexagon', color: 'brand' }"
                />
                <template v-if="isLoading">
                    <CompanyInformationLoader />
                </template>
                <CompanyContactForm v-else :data="companyInformation" />
            </Card>
        </div>
        <Card>
            <div class="company-information__address-card-header">
                <Heading
                    :labelTitle="$t.__('CompanyInformation.addresses.title')"
                    variant="h2"
                    :prefixColoredIcon="{ icon: 'location', color: 'brand' }"
                />
                <CompassButton
                    :label="$t.__('CompanyInformation.addresses.button')"
                    type="secondary"
                    :disabled="isPanelOpen"
                    @click="handleAddAddress"
                />
            </div>
            <CompassAlert
                type="neutral"
                :description="$t.__('CompanyInformation.addresses.alert')"
                customIcon="/ressources/logged/img/icon/svg/info-e-invoicing.svg"
                :showClose="false"
            />
            <CompanyInformationAddressList
                :addresses="addresses"
                :loading="isLoading"
                :isPanelOpen="isPanelOpen"
                @delete="refreshAddresses"
                @update="refreshAddresses"
                @panelOpen="isPanelOpen = true"
                @panelClose="isPanelOpen = false"
            />
        </Card>
    </div>
</template>
<script>
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import CompanyInformationLoader from 'vcomponents/preferences/CompanyInformation/CompanyInformationLoader.vue';
import CompanyInformationFormLocked from 'vcomponents/preferences/CompanyInformation/CompanyInformationFormLocked.vue';
import CompanyInformationForm from 'vcomponents/preferences/CompanyInformation/CompanyInformationForm.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapGetters: corpMapGetters } = createNamespacedHelpers('corp');
import { getAccountInformation } from 'api/account/account';
import { getCountries } from 'api/countries';
import CompanyContactForm from 'vcomponents/preferences/CompanyInformation/CompanyContactForm.vue';
import CompanyInformationAddressList from 'vcomponents/preferences/CompanyInformation/CompanyInformationAddressList.vue';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
import { fetchAccountAddresses } from 'api/addressesService';

export default {
    components: {
        Card,
        CompanyInformationLoader,
        CompanyInformationForm,
        CompanyInformationFormLocked,
        CompanyContactForm,
        CompanyInformationAddressList,
        Heading,
    },
    data() {
        return {
            companyInformation: {},
            countries: [],
            isLoading: false,
            addresses: [],
            isPanelOpen: false,
        };
    },
    computed: {
        ...corpMapGetters({
            companyInformationLocked: 'getCompanyInformationLocked',
        }),
    },
    watch: {
        $route() {
            this.close({ key: 'addressForm' });
        },
    },
    created() {
        this.isLoading = true;
        const accountPromise = getAccountInformation()
            .then((response) => {
                this.companyInformation = response.data;
            })
            .catch(() => {
                this.$pushToast({
                    type: 'error',
                    message: this.$t.__('CompanyInformation.fetch.error'),
                });
            });

        const countriesPromise = getCountries()
            .then((response) => {
                this.countries = response.data.data;
            })
            .catch(() => {
                this.$pushToast({
                    type: 'error',
                    message: this.$t.__('CompanyInformation.countries.fetch.error'),
                });
            });

        const addressesPromise = fetchAccountAddresses()
            .then((response) => {
                this.addresses = response;
            })
            .catch(() => {
                this.$pushToast({
                    type: 'error',
                    message: this.$t.__('CompanyInformation.addresses.fetch_error'),
                });
            });

        Promise.all([accountPromise, countriesPromise, addressesPromise]).finally(() => {
            this.isLoading = false;
        });

        EventBus.on('add-address', (item) => {
            this.addresses.push(item);
        });

        EventBus.on('edit-address', (address) => {
            this.addresses = this.addresses.map((item) => {
                if (item.id !== address.id) {
                    return item;
                }
                return { ...item, ...address };
            });
        });
    },
    beforeUnmount() {
        EventBus.off('add-address');
        EventBus.off('edit-address');
    },
    methods: {
        ...popupPanelMapActions(['open', 'close']),
        handleAddAddress() {
            this.isPanelOpen = true;
            this.open({
                key: 'addressForm',
                props: {
                    address: null,
                    isAccountAddress: true,
                    callback: () => {
                        this.isPanelOpen = false;
                    },
                },
            });
        },
        refreshAddresses(addresses) {
            this.addresses = addresses;
        },
        handleCompanyInformationUpdate(payload) {
            this.companyInformation = payload;
        },
    },
};
</script>
<style lang="scss" scoped>
.company-information {
    &__columns {
        align-items: start;
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: 24px;

        @media screen and (max-width: 1199px) {
            align-items: stretch;
            flex-direction: column;
        }

        &::v-deep {
            .card-container {
                flex: 1;

                & + .card-container {
                    margin-top: 0;
                }
            }
        }
    }

    &__title {
        margin: 0 0 var(--spacing-3);
    }

    &__address-card {
        &-header {
            align-items: start;
            display: flex;
            justify-content: space-between;
        }
    }
}
</style>
